import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getCharacterById, getWorkById } from "@/lib/content";
import { characterComments } from "@/mocks/comments";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function CharacterPage() {
  const { id } = useParams();
  const character = getCharacterById(id);
  const characterWork = getWorkById(character?.workId);

  useDocumentTitle(
    character
      ? `${character.name}角色分析 - 来自${characterWork ? characterWork.title : "影视作品"} | 亲吻姐姐`
      : "角色信息不存在 - 亲吻姐姐",
    character
      ? `${character.name}是《${characterWork ? characterWork.title : "影视作品"}》中的${character.identity}，本文解析其背景、剧情作用与关键成长节点。`
      : "该角色专题可能已下架或链接有误。"
  );

  if (!character) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-user-unfollow-line text-4xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            角色信息不存在或已下架
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            你访问的角色专题可能已被移除，或链接地址不正确。可以回到首页重新浏览。
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const work = getWorkById(character.workId);

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12 lg:px-8">
          <Breadcrumb
            items={[
              { label: "影视分类", to: "/category" },
              ...(work ? [{ label: work.title, to: `/detail/${work.id}` }] : []),
              { label: `${character.name} 角色分析` }
            ]}
          />
          <div className="mt-6 flex flex-col gap-8 sm:flex-row sm:items-start">
            <div className="w-full shrink-0 sm:w-52">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-xl border border-background-200 bg-background-200">
                <img
                  src={character.image}
                  alt={`${character.name} 角色形象`}
                  title={`${character.name} 角色分析 - 来自《${work ? work.title : "作品"}》`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-medium text-accent-800">
                <i className="ri-user-star-line"></i>
                角色分析专题
              </span>
              <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
                {character.name}
              </h1>
              <p className="mt-3 text-sm text-foreground-600 md:text-base">
                {character.identity} · 演员 {character.actor}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {character.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                    {tag}
                  </span>
                ))}
              </div>

              {work && (
                <Link
                  to={`/detail/${work.id}`}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  <i className="ri-film-line"></i>
                  所属作品：《{work.title}》
                  <i className="ri-arrow-right-line"></i>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-950">
              <i className="ri-profile-line text-primary-600"></i>
              角色背景介绍
            </h2>
            <p className="mt-5 text-sm leading-loose text-foreground-700 md:text-base">{character.background}</p>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-950">
              <i className="ri-focus-3-line text-accent-600"></i>
              在剧情中的作用分析
            </h2>
            <p className="mt-5 text-sm leading-loose text-foreground-700 md:text-base">{character.analysis}</p>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-950">
              <i className="ri-route-line text-secondary-600"></i>
              角色动态时间线
            </h2>
            <ol className="mt-6 space-y-6 border-l-2 border-background-200 pl-6">
              {character.timeline.map((item, index) => (
                <li key={item.label} className="relative">
                  <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 ring-4 ring-background-50"></span>
                  <span className="text-xs font-medium text-accent-600">关键节点 {index + 1}</span>
                  <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">{item.label}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.event}</p>
                </li>
              ))}
            </ol>
          </div>

          <aside className="lg:col-span-1">
            {work && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">相关作品</h3>
                <Link to={`/detail/${work.id}`} className="group mt-4 flex gap-3">
                  <div className="h-24 w-20 shrink-0 overflow-hidden rounded-lg bg-background-200">
                    <img
                      src={work.poster}
                      alt={`${work.title} 剧照海报`}
                      title={`${work.title} 剧情介绍`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading text-sm font-bold text-foreground-900 transition-colors group-hover:text-primary-700">
                      {work.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">
                      {work.type} · {work.year}
                    </p>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{work.synopsis}</p>
                  </div>
                </Link>
              </div>
            )}

            <div className="mt-6 rounded-xl border border-background-200 bg-accent-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                <i className="ri-information-line"></i>
                演示说明
              </h3>
              <p className="mt-3 text-xs leading-relaxed text-accent-800">
                本站所有作品、角色与评论均为演示示例，人物为虚构设定，不含真实影星信息。评论区域为静态展示，暂未开放真实互动。
              </p>
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-foreground-950">
            <i className="ri-chat-3-line text-primary-600"></i>
            观众评论
            <span className="text-sm font-normal text-foreground-500">({characterComments.length} 条)</span>
          </h2>
          <div className="mt-6 space-y-4">
            {characterComments.map((comment) => (
              <div key={comment.id} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-base font-bold text-primary-700">
                  {comment.user.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium text-foreground-900">{comment.user}</span>
                    <span className="text-xs text-foreground-400">{comment.time}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-thumb-up-line"></i>
                      {comment.likes}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-chat-1-line"></i>
                      回复
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          {work && (
            <Link
              to={`/detail/${work.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回《{work.title}》详情页
            </Link>
          )}
          <Link
            to="/category"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
          >
            <i className="ri-layout-grid-line"></i>
            浏览更多作品
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}